import { Component, EventEmitter, Inject, Input, Optional, Output } from '@angular/core';

import { AGUViewportService } from '../../viewport/phone';
import { IsRowTap } from './row-tap';

/**
 * The finder result card frame, shared by grants, collaborators and jobs.
 *
 * The mockups' `.gcard` / `.pcard` / `.jcard` are one frame with different
 * content: a chip row whose right gutter is reserved for the bookmark, then
 * the finder's own title, sub-line, facts, "why" band and CTA row, and a
 * quiet `Pin` / `✕ Not for me` footer pinned to the card bottom. Everything
 * finder-specific arrives through the seven projection slots
 * (`[chips] [title] [sub] [facts] [why] [ctas] [footer]`) so this component
 * never needs to know a grant from a job; colour comes from the page band's
 * `--accent` cascade for the same reason.
 *
 * There is deliberately NO match percentage: none of the three mockups prints
 * a score anywhere (parity/grants.md §6, collaborators.md §6, jobs.md §4.8) —
 * the relevance number only orders the results and picks the recommended set.
 */
@Component({
    standalone: false,
    selector: 'agu-item-card',
    templateUrl: './item-card.component.html',
    styleUrl: './item-card.component.scss',
})
export class AGUItemCardComponent {
    @Input() Recommended = false;
    @Input() IsNew = false;
    @Input() Saved = false;
    @Input() Pinned = false;
    @Input() ShowPin = true;
    @Input() ShowDismiss = true;
    /**
     * The mockup's `.pintag` chip — grants.html:758 renders it beside the
     * funder-type badge. A card opts in rather than the chip following `Pinned`
     * everywhere; the grant, job and collaborator cards all do (Jobs QA 2026-10).
     */
    @Input() ShowPinnedChip = false;
    /** A closed/ended item: the mockup dims the whole card (`style="opacity:.66"`, grants.html:755). */
    @Input() Closed = false;
    /** The mockups name the item in the bookmark's label: `Save grant` / `Save collaborator` / `Save job`. */
    @Input() SaveLabel = 'Save';

    @Output() SaveToggled = new EventEmitter<boolean>();
    @Output() PinToggled = new EventEmitter<boolean>();
    @Output() Dismissed = new EventEmitter<void>();
    /**
     * A tap on the card itself — not on one of its controls — on a phone, where the whole row opens its item
     * (P/Member_Hub.html:1011-1019). The finder card wires it to its own Open.
     */
    @Output() RowActivated = new EventEmitter<void>();

    constructor(@Optional() @Inject(AGUViewportService) private readonly viewport: AGUViewportService | null = null) {}

    /** Whether the card is a tappable row: on a phone, and only when someone listens for the tap. */
    get Tappable(): boolean {
        return (this.viewport?.IsPhone ?? false) && this.RowActivated.observed;
    }

    OnRowClick(event: Event): void {
        if (this.Tappable && IsRowTap(event)) this.RowActivated.emit();
    }

    get SaveAriaLabel(): string {
        return this.Saved ? 'Saved, remove' : this.SaveLabel;
    }
}
